'use client';

import { Badge, EntityIcon, Text } from '@terra/ui-design-system';
import { Box, Grid, HStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import { AI_COMPONENT_TYPE_KEYS } from '../utils/aiComponentTypeLabels';
import {
  AI_COMPONENT_TYPE_ICON_COLORS,
  getAiComponentTypeIcon,
} from '../utils/aiComponentIcons';
import { ComponentDetectionMetric } from './ComponentDetectionMetric';
import {
  getMcpComponentIdentityAccessSummary,
  isMcpAssetComponentType,
} from '../utils/aiComponentMetadata';

type AiApplicationAssetRowProps = {
  component: AiComponentDto;
  signalsCount: number;
  isSignalsUnavailable?: boolean;
  hideRoleAndCoverage?: boolean;
  showMcpAccessScope?: boolean;
  onClick?: () => void;
  px?: number;
};

export function AiApplicationAssetRow({
  component,
  signalsCount,
  isSignalsUnavailable = false,
  hideRoleAndCoverage = false,
  showMcpAccessScope = false,
  onClick,
  px = 8,
}: AiApplicationAssetRowProps) {
  const t = useTranslations('aiApplications.assets');
  const tAi = useTranslations('aiApplications');
  const tComponentTypes = useTranslations('aiApplications.componentTypes');
  const Icon = getAiComponentTypeIcon(component.type);
  const iconColors = AI_COMPONENT_TYPE_ICON_COLORS[component.type];
  const mcpAccessSummary =
    showMcpAccessScope && isMcpAssetComponentType(component.type)
      ? getMcpComponentIdentityAccessSummary({ metadata: component.metadata })
      : null;
  const showAccessBadge =
    mcpAccessSummary !== null &&
    mcpAccessSummary.totalCount >= 2 &&
    mcpAccessSummary.deniedCount > 0;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Box
      w="full"
      minH="52px"
      px={px}
      py={2.5}
      borderBottomWidth="1px"
      borderColor="border.neutral.subtle"
      cursor={onClick ? 'pointer' : 'default'}
      bg="transparent"
      _hover={onClick ? { bg: 'bg.neutral.subtle' } : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <Grid
        gap={4}
        alignItems="center"
        w="full"
        gridTemplateColumns={
          hideRoleAndCoverage
            ? showMcpAccessScope
              ? 'minmax(0, 1fr) 96px 120px 120px'
              : 'minmax(0, 1fr) 96px 120px'
            : 'minmax(0, 1fr) 96px 96px 120px 120px'
        }
      >
        <HStack gap={2} alignItems="center" minW={0} flex={1}>
          <Box
            bg={iconColors.bg}
            borderWidth="1px"
            borderColor={iconColors.border}
            color={iconColors.color}
            borderRadius="sm"
            p={1}
            flexShrink={0}
          >
            <Icon size={16} />
          </Box>
          <Text fontSize="sm" fontWeight="medium" color="fg.default" truncate>
            {component.name}
          </Text>
        </HStack>
        <ComponentDetectionMetric
          icon={
            <EntityIcon type="signal" appearance="default" density="compact" />
          }
          label={t('signals')}
          count={signalsCount}
          isUnavailable={isSignalsUnavailable}
        />
        {!hideRoleAndCoverage ? (
          <Text fontSize="sm" color="fg.muted" truncate>
            {component.role ?? tAi('emptyValue')}
          </Text>
        ) : null}
        {!hideRoleAndCoverage ? (
          <Box>
            <Badge colorPalette={component.isInternal ? 'gray' : 'blue'}>
              {component.isInternal ? t('internal') : t('external')}
            </Badge>
          </Box>
        ) : null}
        {showMcpAccessScope ? (
          <Box>
            {showAccessBadge && mcpAccessSummary ? (
              <Badge colorPalette="orange">{t('accessSomeIdentities')}</Badge>
            ) : (
              <Text fontSize="sm" color="fg.muted">
                {tAi('emptyValue')}
              </Text>
            )}
          </Box>
        ) : null}
        <Box textAlign="right">
          {showMcpAccessScope ? (
            <Badge variant="outline" colorPalette="gray">
              {tComponentTypes(AI_COMPONENT_TYPE_KEYS[component.type])}
            </Badge>
          ) : (
            <Text fontSize="sm" color="fg.muted">
              {tComponentTypes(AI_COMPONENT_TYPE_KEYS[component.type])}
            </Text>
          )}
        </Box>
      </Grid>
    </Box>
  );
}
